import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

import { FormCheckbox, FormField, FormSelect } from '../../components/FormField'
import { RecordPicker } from '../../components/RecordPicker'
import { FilterCell, FilterSummary, SortableHeader } from '../../components/TableHeads'
import { describeBookRecord, type BookRecord } from '../../components/bookRecord'
import { TableScroll } from '../../components/TableScroll'
import { historyRow } from '../../components/historyRow'
import { apiErrorMessage } from '../../lib/errors'
import { formatDate, formatMoney, formatNumber } from '../../lib/format'
import { keepingView } from '../../lib/tableAddress'
import type { TableColumn } from '../../lib/tableView'
import { useAddressTableView } from '../../lib/useAddressTableView'
import { filterChoice, useFilterRecord } from '../../lib/useFilterRecord'
import { getBroker, searchBrokers } from '../brokers/api'
import { EQUIPMENT_TYPES, type EquipmentType } from '../carriers/api'
import { discardLoads, listLoads, MAX_DISCARD, type Load, type LoadFilter } from './api'
import { bookFilterFrom, bookSearchFrom } from './bookFilter'
import { equipmentLabel } from './equipment'
import { LoadStandingBadge } from './LoadStandingBadge'

/** A broker's name, asked of the broker itself: an address holds only its id. */
const brokerName = (id: string) => getBroker(id).then((broker) => broker.name)

/** The book's groups: open, then expired, then cancelled — a sorted column sorts within them. */
const STANDINGS: readonly Load['standing'][] = ['Open', 'Expired', 'Cancelled']
const standingRank = (load: Load) => STANDINGS.indexOf(load.standing)

/** Every place a load stops at, in order: what its route column sorts and filters by. */
function routeText(load: Load): string {
  return load.stops.map((stop) => `${stop.city}, ${stop.state}`).join(' → ')
}

/**
 * What the book's columns sort and filter by (TRKC-110). The equipment and the first pickup's
 * state are filtered from the bar above — the server's filters — so the freight heading sorts
 * and its box stays empty. The broker's box finds a company filed under its own name too,
 * which the bar's broker, a record from the book, cannot (TRKC-104).
 */
function loadColumns(t: TFunction): TableColumn<Load>[] {
  return [
    { key: 'route', sortValue: routeText, filter: { kind: 'text', text: routeText } },
    {
      key: 'pickUp',
      sortValue: (load) => load.stops[0]?.windowFrom ?? null,
      filter: { kind: 'onOrAfter', value: (load) => load.stops[0]?.windowFrom ?? null },
    },
    { key: 'freight', sortValue: (load) => equipmentLabel(load, t) },
    { key: 'rate', sortValue: (load) => load.rateUsd, filter: { kind: 'atLeast', value: (load) => load.rateUsd, keepUnknown: true } },
    {
      key: 'broker',
      sortValue: (load) => load.brokerName,
      filter: { kind: 'text', text: (load) => [load.brokerName, load.agentName ?? ''].join(' ') },
    },
    {
      key: 'standing',
      sortValue: standingRank,
      filter: { kind: 'choice', values: STANDINGS, matches: (load, standing) => load.standing === standing },
    },
  ]
}

/** The heading each column's filter box is named after. */
const LOAD_HEADINGS: Record<string, string> = {
  route: 'loads.book.route',
  pickUp: 'loads.book.pickUp',
  freight: 'loads.book.freight',
  rate: 'loads.book.rate',
  broker: 'loads.book.broker',
  standing: 'loads.book.standing',
}

/** No book yet: one array, so the view is not recomputed for an empty one every render. */
const NO_LOADS: Load[] = []

/**
 * The load book: every load captured, soonest pickup first, then the ones whose pickup
 * days have passed, greyed. Cancelled loads are history and only shown when asked for.
 * The server decides the order, the standing and what a filter matches; this page only
 * lays them out.
 */
export function LoadsPage() {
  const { t, i18n } = useTranslation()

  // What the book is narrowed to lives in its address, like the posting board's (TRKC-90,
  // Lino 2026-09-27): going back to the book from a load keeps the filters it was left with.
  // The controls answer from state the page owns, with the address as its copy — the board's
  // reason: the router applies an address change inside a transition, and a box fed straight
  // from it read as never ticked for a moment.
  const [searchParams, setSearchParams] = useSearchParams()
  const address = searchParams.toString()
  const [filter, setFilter] = useState(() => bookFilterFrom(searchParams))

  const [loads, setLoads] = useState<Load[] | null>(null)
  // The broker chosen in the filter's picker, with its name — the address holds only the id.
  const [chosenBroker, setChosenBroker] = useState<BookRecord | null>(null)
  // What the state box holds, apart from the filter it feeds: the book narrows by a whole
  // state or not at all. One letter is half a state, so it narrows nothing rather than
  // finding nothing — and never leaves the book narrowed by a state the box no longer shows.
  const [stateText, setStateText] = useState(filter.pickupState ?? '')
  // Stored unresolved and translated at render, so a language switch re-translates it.
  const [loadFailure, setLoadFailure] = useState<{ error: unknown } | null>(null)
  // The loads picked to discard (TRKC-110 item 4), and what the last discard answered. Only the
  // picked loads the table shows go: one a filter hides is never discarded unseen.
  const [chosen, setChosen] = useState<ReadonlySet<string>>(new Set())
  const [isDiscarding, setIsDiscarding] = useState(false)
  const [discardFailure, setDiscardFailure] = useState<{ error: unknown } | null>(null)
  const [lastDiscard, setLastDiscard] = useState<number | null>(null)
  // Bumped to ask the book again once loads are discarded.
  const [reading, setReading] = useState(0)

  // The address can also change under the page — the menu's plain link, the browser's Back —
  // and the book follows it; following its own writes changes nothing.
  useEffect(() => {
    const next = bookFilterFrom(new URLSearchParams(address))
    setFilter(next)
    setStateText((typed) =>
      next.pickupState === undefined && typed.length < 2 ? typed : (next.pickupState ?? ''),
    )
  }, [address])

  const narrow = (changes: Partial<LoadFilter>) => {
    const next = { ...filter, ...changes }
    setFilter(next)
    // The column view the address holds goes along (TRKC-110).
    setSearchParams((current) => keepingView(bookSearchFrom(next), current), { replace: true })
  }

  const asked = bookSearchFrom(filter).toString()
  const columns = useMemo(() => loadColumns(t), [t])
  const view = useAddressTableView(loads ?? NO_LOADS, columns, standingRank)

  // A broker id in the address that names nobody would narrow the book to nothing under a
  // picker reading "Any broker" (Copilot on #36), so unless it is the one just chosen it is
  // asked of the broker itself (GPT-5.6 Sol on #36) — the board's rule: nothing is asked
  // until it is known, an id nobody has is dropped, one that cannot be checked is kept and
  // shown as chosen.
  const brokerRecord = useFilterRecord(filter.brokerId, chosenBroker, brokerName)
  const holdForBroker = brokerRecord.state === 'checking' || brokerRecord.state === 'gone'

  useEffect(() => {
    if (brokerRecord.state === 'gone') {
      const next = { ...filter, brokerId: undefined }
      setFilter(next)
      setSearchParams((current) => keepingView(bookSearchFrom(next), current), { replace: true })
    }
  }, [brokerRecord.state, filter, setSearchParams])

  // Every filter asks the server again, and answers can land out of order: only the latest
  // request may write, so a slower answer to filters the screen has left — or its failure —
  // never replaces the book the controls describe (GPT-5.6 Sol on #32).
  useEffect(() => {
    if (holdForBroker) {
      return
    }

    let active = true
    setLoadFailure(null)

    listLoads(bookFilterFrom(new URLSearchParams(asked)))
      .then((found) => {
        if (active) {
          setLoads(found)
        }
      })
      .catch((caught: unknown) => {
        if (active) {
          setLoads(null)
          setLoadFailure({ error: caught })
        }
      })

    return () => {
      active = false
    }
  }, [asked, holdForBroker, reading])

  const narrowed = Object.values(filter).some((value) => value !== undefined && value !== '' && value !== false)

  const picked = view.rows.filter((row) => chosen.has(row.id))
  const allPicked = view.rows.length > 0 && picked.length === view.rows.length
  // One discard names at most what the API takes; past it, the button says why instead of
  // sending a request the API would refuse whole (Copilot on #63).
  const tooMany = picked.length > MAX_DISCARD

  function pick(id: string, wanted: boolean) {
    setLastDiscard(null)
    setChosen((current) => {
      const next = new Set(current)
      if (wanted) {
        next.add(id)
      } else {
        next.delete(id)
      }
      return next
    })
  }

  /** Every load the table shows, or none: a filter's hidden loads are never picked by it. */
  function pickAll(wanted: boolean) {
    setLastDiscard(null)
    setChosen(new Set(wanted ? view.rows.map((row) => row.id) : []))
  }

  /**
   * Loads created by mistake (TRKC-110 item 4): asked first, because nothing brings them back.
   * They leave the book — which is asked again — matching and the duplicate check.
   */
  async function discardPicked() {
    const ids = picked.map((row) => row.id)

    if (ids.length === 0 || tooMany || !window.confirm(t('loads.book.discardConfirm', { count: ids.length }))) {
      return
    }

    setIsDiscarding(true)
    setDiscardFailure(null)

    try {
      const answer = await discardLoads(ids)
      // The picks need no clearing: a discarded load is never listed again, and only the picked
      // loads the table shows count.
      setLastDiscard(answer.discarded)
      setReading((current) => current + 1)
    } catch (caught) {
      setDiscardFailure({ error: caught })
    } finally {
      setIsDiscarding(false)
    }
  }

  return (
    <section className="page">
      <header className="page__header page__header--row">
        <div>
          <h1>{t('loads.book.title')}</h1>
          <p className="page__subtitle">{t('loads.book.subtitle')}</p>
        </div>
        <div className="page__actions">
          <Link className="button button--quiet" to="/loads/paste">
            {t('loads.book.paste')}
          </Link>
          <Link className="button" to="/loads/new">
            {t('loads.book.capture')}
          </Link>
        </div>
      </header>

      <div className="toolbar toolbar--filters">
        <FormSelect
          name="equipmentFilter"
          label={t('loads.book.equipment')}
          value={filter.equipment ?? ''}
          onChange={(value) => narrow({ equipment: value === '' ? undefined : (value as EquipmentType) })}
          options={[
            { value: '', label: t('loads.book.anyEquipment') },
            ...EQUIPMENT_TYPES.map((type) => ({ value: type, label: t(`carriers.types.${type}`) })),
          ]}
        />
        <FormField
          name="pickupStateFilter"
          label={t('loads.book.pickupState')}
          value={stateText}
          onChange={(value) => {
            const state = value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 2)
            const pickupState = state.length === 2 ? state : undefined
            setStateText(state)
            if (pickupState !== filter.pickupState) {
              narrow({ pickupState })
            }
          }}
          maxLength={2}
          placeholder="TX"
          width="narrow"
        />
        {/* The broker kept in the address is shown as chosen, as on the board. */}
        <RecordPicker<BookRecord>
          name="brokerFilter"
          label={t('loads.book.broker')}
          value={filterChoice(filter.brokerId, brokerRecord, t('loads.book.linkedBroker'))}
          onChange={(record) => {
            setChosenBroker(record)
            narrow({ brokerId: record?.id })
          }}
          search={searchBrokers}
          describe={(record) => describeBookRecord(record, t)}
          noneLabel={t('loads.book.anyBroker')}
          placeholder={t('loads.book.anyBroker')}
        />
        <FormCheckbox
          name="includeCancelled"
          label={t('loads.book.showCancelled')}
          checked={filter.includeCancelled ?? false}
          onChange={(checked) => narrow({ includeCancelled: checked ? true : undefined })}
        />
      </div>

      {loadFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(loadFailure.error, t, 'loads.book.loadError')}
        </p>
      )}

      {lastDiscard !== null && (
        <p className="page__subtitle" role="status">
          {t('loads.book.discarded', { count: lastDiscard })}
        </p>
      )}

      {discardFailure !== null && (
        <p className="alert" role="alert">
          {apiErrorMessage(discardFailure.error, t, 'loads.book.discardError')}
        </p>
      )}

      {loads !== null && loads.length === 0 && (
        <p className="placeholder">{narrowed ? t('loads.book.noneMatch') : t('loads.book.empty')}</p>
      )}

      {loads !== null && loads.length > 0 && (
        <div className="form__actions loads__discard">
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void discardPicked()}
            disabled={picked.length === 0 || tooMany || isDiscarding}
          >
            {t('loads.book.discardPicked', { count: picked.length })}
          </button>
          {tooMany && <p className="field__hint">{t('loads.book.discardTooMany', { max: MAX_DISCARD })}</p>}
        </div>
      )}

      {loads !== null && loads.length > 0 && (
        <TableScroll label={t('loads.book.title')}>
          <table className="table">
            <caption className="visually-hidden">{t('loads.book.title')}</caption>
            <thead>
              <tr>
                <th scope="col">
                  {/* All or none of the loads shown: mixed when some are picked. */}
                  <input
                    type="checkbox"
                    checked={allPicked}
                    ref={(box) => {
                      if (box !== null) {
                        box.indeterminate = picked.length > 0 && !allPicked
                      }
                    }}
                    disabled={isDiscarding || view.rows.length === 0}
                    aria-label={t('loads.book.pickAll')}
                    onChange={(event) => pickAll(event.target.checked)}
                  />
                </th>
                {columns.map((column) => (
                  <SortableHeader key={column.key} view={view} column={column.key}>
                    {t(LOAD_HEADINGS[column.key] ?? column.key)}
                  </SortableHeader>
                ))}
              </tr>
              <tr className="table__filters">
                <td />
                {columns.map((column) => (
                  <FilterCell
                    key={column.key}
                    view={view}
                    column={column}
                    label={t(LOAD_HEADINGS[column.key] ?? column.key)}
                    options={
                      column.key === 'standing'
                        ? STANDINGS.map((standing) => ({ value: standing, label: t(`loads.standings.${standing}`) }))
                        : undefined
                    }
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {view.rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="placeholder">
                    {t('loads.book.noneMatch')}
                  </td>
                </tr>
              )}
              {view.rows.map((row) => {
                const origin = row.stops[0]
                const destination = row.stops[row.stops.length - 1]

                const route = t('loads.book.routeLine', {
                  origin: origin === undefined ? '' : `${origin.city}, ${origin.state}`,
                  destination: destination === undefined ? '' : `${destination.city}, ${destination.state}`,
                })

                return (
                  <tr key={row.id} className={historyRow(row.standing !== 'Open')}>
                    <td>
                      <input
                        type="checkbox"
                        checked={chosen.has(row.id)}
                        disabled={isDiscarding}
                        aria-label={t('loads.book.pick', { route })}
                        onChange={(event) => pick(row.id, event.target.checked)}
                      />
                    </td>
                    <td>
                      <Link to={`/loads/${row.id}`}>{route}</Link>
                      {row.stops.length > 2 && (
                        <>
                          <br />
                          <span className="muted">{t('loads.book.stops', { count: row.stops.length })}</span>
                        </>
                      )}
                      {row.tripMiles !== null && (
                        <>
                          <br />
                          <span className="muted">
                            {t('loads.book.miles', { formatted: formatNumber(row.tripMiles, i18n.language) })}
                          </span>
                        </>
                      )}
                    </td>
                    <td>
                      {origin?.windowFrom != null &&
                        (origin.windowTo === null || origin.windowTo === origin.windowFrom
                          ? formatDate(origin.windowFrom, i18n.language)
                          : t('loads.book.window', {
                              from: formatDate(origin.windowFrom, i18n.language),
                              to: formatDate(origin.windowTo, i18n.language),
                            }))}
                    </td>
                    <td>
                      {equipmentLabel(row, t)}
                      <br />
                      <span className="muted">
                        {t('loads.book.freightLine', {
                          loadType: t(`availability.loadTypes.${row.loadType}`),
                          pounds: formatNumber(row.weightPounds, i18n.language),
                        })}
                        {row.lengthFeet !== null && ` · ${t('loads.book.feet', { feet: row.lengthFeet })}`}
                      </span>
                    </td>
                    <td className="table__numeric">
                      {row.rateUsd === null ? (
                        <span className="muted">{t('loads.book.noRate')}</span>
                      ) : (
                        <>
                          {formatMoney(row.rateUsd, 'USD', i18n.language)}
                          {row.ratePerMileUsd !== null && (
                            <>
                              <br />
                              <span className="muted">
                                {t('loads.book.perMile', { formatted: formatMoney(row.ratePerMileUsd, 'USD', i18n.language) })}
                              </span>
                            </>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {row.brokerName}
                      {/* Filed under the company's name: the broker is registered when one
                          of its loads is booked (TRKC-104). */}
                      {row.brokerId === null && (
                        <>
                          <br />
                          <span className="muted">{t('loads.book.notInBook')}</span>
                        </>
                      )}
                      {row.agentName !== null && (
                        <>
                          <br />
                          <span className="muted">{row.agentName}</span>
                        </>
                      )}
                    </td>
                    <td>
                      <LoadStandingBadge standing={row.standing} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      {loads !== null && loads.length > 0 && <FilterSummary view={view} />}
    </section>
  )
}
